import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import Button from "../Button";
import EN from "../../locales/en.json";

const FilterTariffsModal = ({ open, setOpen, filters, setFilters }) => {
  const paymentTypeMap = {
    "Monthly Direct Debit":
      EN.quotes.filter_tariffs.payment_type.monthly_direct_debit,
    "Pay On Receipt Of Bill":
      EN.quotes.filter_tariffs.payment_type.pay_on_receipt,
    Prepayment: EN.quotes.filter_tariffs.payment_type.prepayment,
    "Quarterly Direct Debit":
      EN.quotes.filter_tariffs.payment_type.quarterly_direct_debit,
  };

  return (
    <Dialog className="relative z-10" open={open} onClose={setOpen}>
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-dark-gray bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full sm:justify-center pl-4 py-4 text-center items-start sm:p-0">
          <DialogPanel
            transition
            className="relative sm:my-8 w-full sm:max-w-[475px] transform overflow-hidden transition-all data-[closed]:translate-x-8 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in data-[closed]:sm:translate-x-0 data-[closed]:sm:scale-95"
          >
            <div className="rounded-tl-[24px] rounded-bl-[24px] sm:rounded-[24px] bg-white text-left shadow-xl overflow-hidden p-8">
              <div className="flex justify-between items-start pb-6 border-b border-[#afb6b9]">
                <h2 className="text-[22px] font-bold">
                  {EN.quotes.filter_tariffs.title}
                </h2>
                <button
                  className="w-[30px] h-[30px] flex items-center justify-center rounded-full bg-[#E7E9EA]"
                  onClick={() => setOpen(false)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    className="size-5 text-black"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18 18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="py-6 space-y-6">
                <div className="border-b border-[#afb6b9] pb-6">
                  <h3 className="text-md font-bold leading-[1.5] mb-4">
                    {EN.quotes.filter_tariffs.tariff_availability}
                  </h3>

                  <div className="space-y-4">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        value={true}
                        checked={!!filters?.onlyShowSwitchable}
                        onChange={() =>
                          setFilters({
                            ...filters,
                            onlyShowSwitchable: true,
                          })
                        }
                        className="size-5 text-primary focus:ring-primary"
                      />
                      <span>{EN.quotes.only_show_switchable}</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        value={false}
                        checked={!filters?.onlyShowSwitchable}
                        onChange={() =>
                          setFilters({
                            ...filters,
                            onlyShowSwitchable: false,
                          })
                        }
                        className="size-5 text-primary focus:ring-primary"
                      />
                      <span>{EN.quotes.include_non_switchable_tariffs}</span>
                    </label>
                  </div>
                </div>

                <div className="border-b border-[#afb6b9] pb-6">
                  <h3 className="text-md font-bold leading-[1.5] mb-4">
                    {EN.quotes.filter_tariffs.contract_type.title}
                  </h3>
                  <div className="space-y-2">
                    {[
                      EN.quotes.filter_tariffs.contract_type.fixed,
                      EN.quotes.filter_tariffs.contract_type.variable,
                    ].map((type) => (
                      <label
                        key={type}
                        className="flex items-center gap-3 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={filters.contractTypes.includes(type)}
                          onChange={(e) => {
                            const updatedTypes = e.target.checked
                              ? [...filters.contractTypes, type]
                              : filters.contractTypes.filter((t) => t !== type);
                            setFilters({
                              ...filters,
                              contractTypes: updatedTypes,
                            });
                          }}
                          className="size-5 rounded-[4px] text-primary focus:ring-primary"
                        />
                        <span>{type}</span>
                      </label>
                    ))}
                  </div>

                  <Disclosure>
                    {({ open }) => (
                      <>
                        <DisclosureButton className="mt-4 flex items-center gap-2 text-primary">
                          <ChevronDownIcon
                            className={`w-4 h-4 transition-transform ${
                              open ? "rotate-180 transform" : ""
                            }`}
                          />
                          <span className="underline">
                            {
                              EN.quotes.filter_tariffs.contract_type
                                .explanation_button
                            }
                          </span>
                        </DisclosureButton>
                        <DisclosurePanel className="mt-4">
                          <div className="space-y-4">
                            <p>
                              <strong>
                                {EN.quotes.filter_tariffs.contract_type.fixed}
                              </strong>{" "}
                              {
                                EN.quotes.filter_tariffs.contract_type
                                  .fixed_explanation
                              }
                            </p>
                            <p>
                              <strong>
                                {
                                  EN.quotes.filter_tariffs.contract_type
                                    .variable
                                }
                              </strong>{" "}
                              {
                                EN.quotes.filter_tariffs.contract_type
                                  .variable_explanation
                              }
                            </p>
                          </div>
                        </DisclosurePanel>
                      </>
                    )}
                  </Disclosure>
                </div>

                <div>
                  <h3 className="text-md font-bold leading-[1.5] mb-4">
                    {EN.quotes.filter_tariffs.payment_type.title}
                  </h3>
                  <div className="space-y-2">
                    {Object.values(paymentTypeMap).map((type) => (
                      <label
                        key={type}
                        className="flex items-center gap-3 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={filters.paymentTypes.includes(type)}
                          onChange={(e) => {
                            const updatedTypes = e.target.checked
                              ? [...filters.paymentTypes, type]
                              : filters.paymentTypes.filter((t) => t !== type);
                            setFilters({
                              ...filters,
                              paymentTypes: updatedTypes,
                            });
                          }}
                          className="size-5 rounded-[4px] text-primary focus:ring-primary"
                        />
                        <span>{type}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="pt-6 border-t border-[#afb6b9] space-y-4">
                <Button fullWidth onClick={() => setOpen(false)}>
                  {EN.quotes.filter_tariffs.buttons.apply}
                </Button>
                <Button
                  fullWidth
                  variant="secondary"
                  onClick={() =>
                    setFilters({
                      onlyShowSwitchable: false,
                      contractTypes: [],
                      paymentTypes: [],
                    })
                  }
                >
                  {EN.quotes.filter_tariffs.buttons.reset}
                </Button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

export default FilterTariffsModal;
